import type {
  AuditEvent,
  Invitation,
  Opportunity,
  OpportunityStatus,
  OpportunityUpdate,
  UserSession,
} from "../types";

type DemoClient = readonly [market: string, clientName: string];

const demoClientRoster: DemoClient[] = [
  ["Australia", "Fun Planet"],
  ["Australia", "Funlab"],
  ["Belgium", "Tero/Hadrena"],
  ["Belgium", "UNIBOX"],
  ["Bolivia / Paraguay / Uruguay", "SkyGames"],
  ["Canada", "Harminder Parmar"],
  ["Chile / Mexico", "Happyland"],
  ["Finland", "SuperPark"],
  ["India", "Dave & Buster's India"],
  ["India", "HM Leisure"],
  ["India", "Nazara"],
  ["India", "Prasuk Jain Hospitality"],
  ["Kazakhstan", "My Kids"],
  ["Kenya", "Pins Entertainment"],
  ["Lebanon", "Robert's Group Holding"],
  ["Nigeria", "Xnatives"],
  ["Paraguay", "Sky Games"],
  ["Philippines", "Ayala"],
  ["Philippines", "Fantasy World"],
  ["Philippines", "SM Entertainment"],
  ["Philippines", "Dave & Buster's (Philippines) - The Bistro Group"],
  ["Poland", "MKBowling"],
  ["Romania", "Catalin"],
  ["Russia", "Zamania Group"],
  ["Saudi Arabia", "Al Amir"],
  ["Saudi Arabia", "Al Othaim Entertainment"],
  ["Saudi Arabia", "BAAN UAE"],
  ["Saudi Arabia", "Funtura KSA"],
  ["Saudi Arabia", "Sala Group"],
  ["Singapore", "Kidztopia (Singapore)"],
  ["South Africa", "The Magic Company"],
  ["Sweden", "Risco Holdings - Mikael Rhawi"],
  ["Switzerland", "Kids Fun Park"],
  ["Thailand", "BOUNCE INC (THAILAND) COMPANY LIMITED"],
  ["Turkey", "Reha (Turkey)"],
  ["United Arab Emirates", "Antic's Land"],
  ["United Arab Emirates", "BAAN (Abu Dhabi)"],
  ["United Arab Emirates", "Leo & Loona"],
  ["United Arab Emirates", "Black Fortune Investments/Paul/Mumuso"],
  ["United States", "Dave & Buster's"],
  ["United States", "Urban Air"],
  ["United States", "Richie Boston"],
  ["United States", "Playzone (Arizona)"],
  ["United States", "Blastoff Minnesota"],
  ["United States", "Shock Trampoline"],
  ["United States", "Pinstack"],
  ["United States", "Castlecade"],
  ["United States", "Five Star Parks"],
  ["United States", "Volume Solutions"],
  ["United States", "Flying Squirrel"],
  ["Vietnam", "Tiniworld"],
];

const demoStatuses: OpportunityStatus[] = [
  "Contacted",
  "Discovery in progress",
  "Solution design",
  "Proposal submitted",
  "Client reviewing",
  "Commercial negotiation",
  "Contracting",
  "Payment pending",
  "Paid - delivery pending",
  "Delivery in progress",
  "Live - post-launch support",
  "On hold",
];

function dateFromToday(offsetDays: number): string {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + offsetDays);
  return date.toISOString().slice(0, 10);
}

export const demoSupplierSession: UserSession = {
  accessToken: "demo-esac-supplier-token",
  displayName: "ESAC Demo",
  email: "demo@esac.local",
  role: "supplier_updater",
  supplierOrganizationId: "00000000-0000-0000-0000-000000000001",
};

export const demoOpportunities: Opportunity[] = demoClientRoster.map(
  ([market, clientName], index) => {
    const isOverdue = index < 8;
    return {
      id: `demo-opp-${index + 1}`,
      clientReference: {
        id: `demo-client-${index + 1}`,
        displayName: clientName,
        market,
      },
      projectName: "Partnership opportunity follow-up",
      projectSummary:
        "Demo project record for an ESAC partnership follow-up. This screen contains only the project essentials shared with ESAC.",
      status: demoStatuses[index % demoStatuses.length],
      nextCheckInOn: dateFromToday(
        isOverdue ? -((index % 5) + 1) : (index % 14) + 1,
      ),
      reviewOn: index % 6 === 5 ? dateFromToday(14) : undefined,
      assignedOn: dateFromToday(-45 - index),
      isOverdue,
      supplierName: "ESAC",
      lastUpdateNote:
        index % 3 === 0 ? "Demo: follow-up is being prepared." : undefined,
      updatedAt: new Date(Date.now() - (index + 1) * 86_400_000).toISOString(),
    };
  },
);

export const demoInvitations: Invitation[] = [
  {
    id: "demo-invite-1",
    email: "owner@esac.demo",
    supplierName: "ESAC",
    expiresAt: new Date(Date.now() + 72 * 3_600_000).toISOString(),
    status: "pending",
  },
];

export const demoAuditEvents: AuditEvent[] = [
  {
    id: "demo-audit-1",
    occurredAt: new Date(Date.now() - 45 * 60_000).toISOString(),
    actorName: "Demo automation",
    action: "whatsapp.simulated",
    entityType: "ReminderRun",
    summary: "Demo WhatsApp reminder prepared for the ESAC group",
    attachments: [],
  },
  {
    id: "demo-audit-2",
    occurredAt: new Date(Date.now() - 3 * 3_600_000).toISOString(),
    actorName: "Bridge AI Gaming",
    action: "opportunities.assigned",
    entityType: "Opportunity",
    summary: "51 demo client opportunities assigned to ESAC",
    attachments: [
      {
        id: "demo-attachment-portfolio",
        fileName: "ESAC-demo-portfolio-summary.pdf",
        contentType: "application/pdf",
        sizeBytes: 428_000,
        downloadUrl:
          "data:text/plain;charset=utf-8,Demo%20attachment%20for%20the%20ESAC%20recording",
      },
    ],
  },
];

export const demoOpportunityUpdates: Record<string, OpportunityUpdate[]> = {
  "demo-opp-1": [
    {
      id: "demo-update-1",
      opportunityId: "demo-opp-1",
      occurredAt: new Date(Date.now() - 2 * 86_400_000).toISOString(),
      actorName: "ESAC Demo",
      previousStatus: "Introduced",
      status: "Contacted",
      nextCheckInOn: dateFromToday(3),
      note: "Initial discussion completed. Preparing the next discovery call.",
      attachments: [
        {
          id: "demo-attachment-1",
          fileName: "Fun-Planet-meeting-summary.pdf",
          contentType: "application/pdf",
          sizeBytes: 382_000,
          downloadUrl:
            "data:text/plain;charset=utf-8,Demo%20meeting%20summary%20attachment",
        },
        {
          id: "demo-attachment-2",
          fileName: "initial-requirements.docx",
          contentType:
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          sizeBytes: 194_000,
          downloadUrl:
            "data:text/plain;charset=utf-8,Demo%20requirements%20attachment",
        },
      ],
    },
  ],
};
